// components/ui/Tooltip.tsx
// UI Phase 1 primitive: tooltip on Radix Tooltip (hover + focus, Escape to
// dismiss, aria-describedby wiring). Each Tooltip carries its own Provider:
// Radix resolves the NEAREST provider, so an app-root <Tooltip.Provider>
// would be inert for these — per-instance delay (300ms) is the contract.
// Trade-off: the cross-tooltip skip-delay warm-up can't engage; revisit in
// Phase 2 if tooltip-dense surfaces (e.g. the planner grid) need it.

import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';
import { cn } from './cn';

export interface TooltipProps {
  /** Trigger element (rendered via asChild — must accept a ref) */
  children: ReactNode;
  content: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

const Tooltip = ({ children, content, side = 'top' }: TooltipProps) => (
  <RadixTooltip.Provider delayDuration={300}>
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          className={cn(
            'z-50 max-w-xs rounded-control bg-ink text-surface-card text-xs font-medium px-2.5 py-1.5 shadow-md',
            'animate-fade-in'
          )}
        >
          {content}
          <RadixTooltip.Arrow className="fill-[rgb(var(--ink-1))]" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  </RadixTooltip.Provider>
);

export default Tooltip;
